import {
  IconCancel,
  IconCloudDownload,
  IconEye,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { createColumnHelper } from '@tanstack/react-table';
import { Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { UserDocument } from '@api/document/model/types';
import { dateFromString } from '@utils/utils.ts';
import { BillingHistory } from '@app/types/payment/billing';
import MainTableRowActions from '@components/MainTable/MainTableRowActions';
import { AllDocumentsTableMeta } from '@app/types/overrides/table-meta';
import { Status } from '@api/notifications/model/enums';
import { DocumentType } from '@utils/enums.ts';
import { routes } from '@shared/constants/paths';
import TableDemandCell from '@features/documents/components/TableDemandCell';
import DepositionClientCell from '@features/documents/components/DepositionClientCell';
import DocumentStatusCell from '@features/documents/components/DocumentStatusCell';
import SearchMatchSnippet from '@features/documents/components/SearchMatchSnippet';
import { patchCase } from '@api/case/apiService';

const columnHelper = createColumnHelper<UserDocument>();
const excludedStatuses = [Status.Pending, Status.InProgress];
const viewableUnfinishedStatuses = [Status.Draft, Status.Failed];
export default function createDocumentColumns(
  loadDocuments: () => void,
  navigate: (path: string) => void,
  documentType?: string,
  extraData?: {
    paymentHistory?: BillingHistory[];
    companyMembers?: any[];
    searchTerm?: string;
  }
) {
  const columns: any = [];

  // Column 1: Client Name (for all tabs)
  columns.push(
    columnHelper.accessor('name', {
      id: 'name',
      header: documentType === 'cases' ? 'Client Name' : 'Client',
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <>
          {original.type === DocumentType.DEPOSITION_SUMMARY ? (
            <DepositionClientCell
              clientName={original.name}
              deponentName={original.deponent_name}
              onClickCell={() =>
                navigate(routes.myDocuments.depositionSummary.view(original.id))
              }
            />
          ) : (
            <TableDemandCell
              id={original.id}
              documentType={original.type}
              value={original.name}
              onSuccess={loadDocuments}
              isDocument={(original.type as any) !== 'Case'}
              updateFunction={
                (original.type as any) === 'Case'
                  ? (
                      id: number,
                      payload: any,
                      callback: (response: any) => void
                    ) =>
                      new Promise<void>((resolve) => {
                        const nextName =
                          (payload && (payload.filename ?? payload.name)) ?? '';
                        patchCase(
                          id,
                          { client_name: String(nextName) },
                          (resp) => {
                            callback(resp as any);
                            resolve();
                          },
                          (err) => {
                            console.error(
                              '[Case rename] Failed to update case name',
                              err
                            );
                            resolve();
                          }
                        );
                      })
                  : undefined
              }
              onClickCell={() => {
                if (original.type === DocumentType.DEMAND_LETTER) {
                  navigate(routes.myDocuments.demandLetter.view(original.id));
                } else if (original.type === DocumentType.USE_CASE) {
                  navigate(routes.myDocuments.useCase.view(original.id));
                } else if (original.type === DocumentType.DEPOSITION_SUMMARY) {
                  navigate(
                    routes.myDocuments.depositionSummary.view(original.id)
                  );
                } else if ((original.type as any) === 'Case') {
                  navigate(`/cases/edit/${original.id}`);
                }
              }}
            />
          )}
          <SearchMatchSnippet
            match={original.search_match}
            term={extraData?.searchTerm}
          />
        </>
      ),
    })
  );

  // Column 2: Document Type or Case Type (depending on tab)
  columns.push(
    columnHelper.accessor('type_name', {
      id: 'type_name',
      header: documentType === 'cases' ? 'Case Type' : 'Document Type',
      size: 200,
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <Typography variant="body2">{original.type_name}</Typography>
      ),
    })
  );

  // Column 3: Date Modified (formerly Last Modified)
  columns.push(
    columnHelper.accessor(
      (row) => {
        const dateValue = (row as any).updated_at || (row as any).last_modified;
        return dateValue ? new Date(dateValue).getTime() : 0;
      },
      {
        id: 'updated_at',
        header: 'Date Modified',
        size: 200,
        enableSorting: true,
        enableResizing: false,
        sortingFn: 'basic',
        cell: ({ row: { original } }) => {
          const dateValue = original.updated_at || original.last_modified;
          return (
            <Typography variant="body2">
              {dateValue ? dateFromString(new Date(dateValue)) : '-'}
            </Typography>
          );
        },
      }
    )
  );

  // Column 4: Date of Creation
  columns.push(
    columnHelper.accessor(
      (row) => {
        const dateValue = (row as any).created_at || (row as any).date_created;
        return dateValue ? new Date(dateValue).getTime() : 0;
      },
      {
        id: 'created_at',
        header: documentType === 'cases' ? 'Date of Creation' : 'Date Created',
        size: 200,
        enableSorting: true,
        enableResizing: false,
        sortingFn: 'basic',
        cell: ({ row: { original } }) => {
          const dateValue = original.created_at || original.date_created;
          return (
            <Typography variant="body2">
              {dateValue ? dateFromString(new Date(dateValue)) : '-'}
            </Typography>
          );
        },
      }
    )
  );

  // Column 5: User (formerly Creator)
  const creatorColumn = columnHelper.accessor('creator_name', {
    id: 'creator_name',
    header: 'User',
    size: 200,
    enableSorting: true,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">
        {original.creator_name ||
          original.creator ||
          original.creator_email ||
          '-'}
      </Typography>
    ),
  });

  // Add Creator column for all users (not just superusers)
  columns.push(creatorColumn);

  // Column 6: Status (only for non-Case tabs)
  if (documentType !== 'cases') {
    columns.push(
      columnHelper.accessor('generation_status', {
        id: 'generation_status',
        header: 'Status',
        size: 200,
        enableSorting: true,
        enableResizing: false,
        cell: ({ row: { original } }) => (
          <DocumentStatusCell
            status={original.generation_status}
            documentId={original.id}
            documentType={original.type}
            documentTypeName={original.type_name}
            failureReason={original.failure_reason}
          />
        ),
      })
    );
  }

  // Add Actions column
  columns.push(
    columnHelper.accessor('id', {
      id: 'id',
      header: documentType === 'cases' ? 'Actions' : 'Action',
      enableSorting: false,
      enableResizing: false,
      cell: ({
        row: { original },
        table: {
          options: { meta },
        },
      }) => {
        const tableMeta = meta as AllDocumentsTableMeta<typeof original>;
        // A colleague's shared row (ZLAYT-1625) is read-only here: only its
        // owner edits, deletes or cancels it. Older responses carry no flag.
        const isOwn = original.is_own !== false;

        // Use pre-fetched billing data to avoid redundant requests
        const billingData = extraData?.paymentHistory || [];
        const companyMembers = extraData?.companyMembers || [];

        const relevantBilling = (() => {
          if (
            original.generation_status !== Status.Completed ||
            !billingData.length
          ) {
            return null;
          }

          const documentDate = new Date(original.created_at).getTime();

          return billingData.find((billing: BillingHistory) => {
            const periodParts = billing.period.split(' - ');
            if (periodParts.length !== 2) return false;

            const periodStart = new Date(periodParts[0]).getTime();
            const periodEnd = new Date(periodParts[1]).getTime();

            return documentDate >= periodStart && documentDate <= periodEnd;
          });
        })();

        const showInvoiceButton =
          !!relevantBilling &&
          (relevantBilling.description.toLowerCase().includes('tier 1') ||
            relevantBilling.description.toLowerCase().includes('tier 2'));

        return (
          <MainTableRowActions>
            {/* Invoice button - only shown when subscription check passes */}
            {showInvoiceButton && (
              <Tooltip title="Invoice">
                <IconButton
                  onClick={async () => {
                    try {
                      if (!relevantBilling) {
                        console.error(
                          'No subscription found for this document'
                        );
                        return;
                      }

                      // Determine tier from description
                      let rate = 0;
                      if (
                        relevantBilling.description
                          .toLowerCase()
                          .includes('tier 1')
                      ) {
                        rate = 99.9;
                      } else if (
                        relevantBilling.description
                          .toLowerCase()
                          .includes('tier 2')
                      ) {
                        rate = 66.63;
                      } else {
                        console.error('Unknown subscription tier');
                        return;
                      }

                      // Use pre-fetched company members to get creator name
                      let creatorName = '';
                      const creator = companyMembers.find(
                        (member: any) =>
                          member.user_email === original.creator_email
                      );
                      if (creator) {
                        creatorName = creator.user_name;
                      }

                      // Generate PDF with the billing data, rate, document data, and creator name
                      const { pdf } = await import('@react-pdf/renderer');
                      const InvoicePDF = (await import('./InvoicePDF')).default;
                      const blob = await pdf(
                        <InvoicePDF
                          billingData={relevantBilling}
                          rate={rate}
                          document={original}
                          creatorName={creatorName}
                        />
                      ).toBlob();

                      // Download the PDF
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = relevantBilling?.invoice_number
                        ? `invoice-${relevantBilling.invoice_number}.pdf`
                        : 'invoice.pdf';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Error generating invoice:', error);

                      // Use pre-fetched company members as fallback
                      let creatorName = '';
                      const creator = companyMembers.find(
                        (member: any) =>
                          member.user_email === original.creator_email
                      );
                      if (creator) {
                        creatorName = creator.user_name;
                      }

                      // Fallback to generate invoice with document data but without billing data
                      const { pdf } = await import('@react-pdf/renderer');
                      const InvoicePDF = (await import('./InvoicePDF')).default;
                      const blob = await pdf(
                        <InvoicePDF
                          document={original}
                          creatorName={creatorName}
                        />
                      ).toBlob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'invoice.pdf';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    }
                  }}
                >
                  <ReceiptLongIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* View and Download buttons - shown for completed documents regardless of subscription */}
            {original.generation_status === Status.Completed && (
              <>
                <Tooltip title="View Document">
                  <IconButton
                    onClick={() => {
                      tableMeta?.handleViewRow &&
                        tableMeta?.handleViewRow(original);
                    }}
                  >
                    <IconEye />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Document">
                  <IconButton
                    onClick={() => {
                      tableMeta?.handleDownloadRow &&
                        tableMeta?.handleDownloadRow(original);
                    }}
                  >
                    <IconCloudDownload />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {isOwn &&
              original.generation_status === Status.Pending &&
              (original.type === DocumentType.DEMAND_LETTER ||
                original.type === DocumentType.USE_CASE ||
                original.type === DocumentType.DEPOSITION_SUMMARY) && (
                <Tooltip title="Cancel generation">
                  <IconButton
                    onClick={() => {
                      tableMeta?.handleCancelRow &&
                        tableMeta?.handleCancelRow(original);
                    }}
                  >
                    <IconCancel />
                  </IconButton>
                </Tooltip>
              )}
            {/* ZLAYT-1487: a draft that was never generated, or a failed
                one, can still be opened; its page offers Edit and Delete. */}
            {viewableUnfinishedStatuses.includes(
              original.generation_status as Status
            ) && (
              <Tooltip title="View Document">
                <IconButton
                  onClick={() => {
                    tableMeta?.handleViewRow &&
                      tableMeta?.handleViewRow(original);
                  }}
                >
                  <IconEye />
                </IconButton>
              </Tooltip>
            )}
            {isOwn &&
              !excludedStatuses.includes(
                original.generation_status as Status
              ) && (
                <>
                  <Tooltip title="Edit Document">
                    <IconButton
                      onClick={() => {
                        tableMeta?.handleEditRow &&
                          tableMeta?.handleEditRow(original);
                      }}
                    >
                      <IconPencil />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Document">
                    <IconButton
                      onClick={() => {
                        tableMeta?.handleDeleteRow &&
                          tableMeta?.handleDeleteRow(original);
                      }}
                    >
                      <IconTrash />
                    </IconButton>
                  </Tooltip>
                </>
              )}
          </MainTableRowActions>
        );
      },
    })
  );

  // Reorder columns for non-Case tabs to:
  // Client, Document Type, Status, Date Created, Date Modified, User, Action
  if (documentType !== 'cases') {
    const order = [
      'name',
      'type_name',
      'generation_status',
      'created_at',
      'updated_at',
      'creator_name',
      'id',
    ];
    const reordered = order
      .map((key) => columns.find((c: any) => (c as any).id === key))
      .filter(Boolean);
    return reordered as any;
  }

  return columns;
}
